/**
 * @license
 * Copyright Futuro Team. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { getDeepFromObject, NbAuthSimpleToken, NbAuthStrategyOptions, NbAuthTokenClass } from '@nebular/auth';

export interface NbGraphqlPasswordStrategyModule {
  alwaysFail?: boolean;
  redirect?: {
    success?: string | null;
    failure?: string | null;
  };
  requireValidToken?: boolean;
  defaultErrors?: string[];
  defaultMessages?: string[];
}

export interface NbGraphqlPasswordStrategyReset extends NbGraphqlPasswordStrategyModule {
  resetPasswordTokenKey?: string;
}

export interface NbGraphqlPasswordStrategyToken {
  class?: NbAuthTokenClass;
  key?: string;
  getter?: (...options: unknown[]) => unknown;
}

export interface NbGraphqlPasswordStrategyMessage {
  key?: string;
  getter?: (...options: unknown[]) => unknown;
}

export class NbGraphqlPasswordAuthStrategyOptions extends NbAuthStrategyOptions {
  public login?: boolean | NbGraphqlPasswordStrategyModule = {
    alwaysFail: false,
    requireValidToken: false,
    redirect: {
      success: '/',
      failure: null,
    },
    defaultErrors: ['Login/Email combination is not correct, please try again.'],
    defaultMessages: ['You have been successfully logged in.'],
  };
  public register?: boolean | NbGraphqlPasswordStrategyModule = {
    alwaysFail: false,
    requireValidToken: false,
    redirect: {
      success: '/',
      failure: null,
    },
    defaultErrors: ['Something went wrong, please try again.'],
    defaultMessages: ['You have been successfully registered.'],
  };
  public requestPass?: boolean | NbGraphqlPasswordStrategyModule = {
    redirect: {
      success: '/',
      failure: null,
    },
    defaultErrors: ['Something went wrong, please try again.'],
    defaultMessages: ['Reset password instructions have been sent to your email.'],
  };
  public resetPass?: boolean | NbGraphqlPasswordStrategyReset = {
    redirect: {
      success: '/',
      failure: null,
    },
    resetPasswordTokenKey: 'reset_password_token',
    defaultErrors: ['Something went wrong, please try again.'],
    defaultMessages: ['Your password has been successfully changed.'],
  };
  public logout?: boolean | NbGraphqlPasswordStrategyReset = {
    alwaysFail: false,
    redirect: {
      success: '/',
      failure: null,
    },
    defaultErrors: ['Something went wrong, please try again.'],
    defaultMessages: ['You have been successfully logged out.'],
  };
  public refreshToken?: boolean | NbGraphqlPasswordStrategyModule = {
    requireValidToken: false,
    redirect: {
      success: null,
      failure: null,
    },
    defaultErrors: ['Something went wrong, please try again.'],
    defaultMessages: ['Your token has been successfully refreshed.'],
  };
  public token?: NbGraphqlPasswordStrategyToken = {
    class: NbAuthSimpleToken,
    key: 'data.login.token',
    getter: (_module: string, res: any, options: NbGraphqlPasswordAuthStrategyOptions) => {
      return getDeepFromObject(res, options.token.key) as unknown;
    },
  };
  public errors?: NbGraphqlPasswordStrategyMessage = {
    key: 'data.errors',
    getter: (module: string, res: HttpErrorResponse, options: NbGraphqlPasswordAuthStrategyOptions) =>
      getDeepFromObject(res.error, options.errors.key, options[module].defaultErrors),
  };
  public messages?: NbGraphqlPasswordStrategyMessage = {
    key: 'data.messages',
    getter: (
      module: string,
      res: HttpResponse<Record<string, unknown>>,
      options: NbGraphqlPasswordAuthStrategyOptions,
    ) => getDeepFromObject(res, options.messages.key, (options[module] || {}).defaultMessages),
  };
  public validation?: {
    password?: {
      required?: boolean;
      minLength?: number | null;
      maxLength?: number | null;
      regexp?: string | null;
    };
    email?: {
      required?: boolean;
      regexp?: string | null;
    };
  };
}

export const graphqlPasswordStrategyOptions: NbGraphqlPasswordAuthStrategyOptions =
  new NbGraphqlPasswordAuthStrategyOptions();
